import { useState } from 'react';
import { X, Calendar, User, Phone, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { bookingFormSchema, sanitizeInput } from '@/lib/validation';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
  device?: string;
  issue?: string;
  date?: string;
  time?: string;
}

const BookingModal = ({ isOpen, onClose }: BookingModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    device: '',
    issue: '',
    date: '',
    time: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const devices = ['iPhone', 'MacBook', 'iPad', 'Apple Watch', 'Other'];
  const timeSlots = ['10:00 AM', '11:00 AM', '12:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM'];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate form data with Zod schema
    const result = bookingFormSchema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FormErrors;
        if (field && !fieldErrors[field]) {
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error('Please fix the form errors before submitting.');
      return;
    }

    const validData = result.data;
    
    // Sanitize all text inputs
    const sanitizedName = sanitizeInput(validData.name);
    const sanitizedPhone = sanitizeInput(validData.phone);
    const sanitizedEmail = validData.email ? sanitizeInput(validData.email) : '';
    const sanitizedIssue = sanitizeInput(validData.issue);
    
    // Format date for display
    const formattedDate = validData.date 
      ? new Date(validData.date).toLocaleDateString('en-IN', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      : '';
    
    // Create WhatsApp message with sanitized and encoded data
    const message = `*📱 New Appointment Request*

*Name:* ${sanitizedName}
*Phone:* ${sanitizedPhone}
*Email:* ${sanitizedEmail || 'Not provided'}
*Device:* ${validData.device}
*Issue:* ${sanitizedIssue}
*Preferred Date:* ${formattedDate}
*Preferred Time:* ${validData.time}

_Sent from iFix Apple Care website_`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919798653591?text=${encodedMessage}`, '_blank');
    toast.success('Redirecting to WhatsApp! Please send the message to confirm your booking.');
    onClose();
    setFormData({ name: '', phone: '', email: '', device: '', issue: '', date: '', time: '' });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-foreground/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-background rounded-3xl shadow-apple-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="sticky top-0 bg-background p-6 border-b border-border flex items-center justify-between rounded-t-3xl">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Book Appointment</h2>
            <p className="text-sm text-muted-foreground">Schedule your device repair</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                required
                maxLength={100}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full h-12 pl-12 pr-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.name ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                placeholder="Enter your name"
              />
            </div>
            {errors.name && <p className="text-destructive text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="tel"
                required
                maxLength={10}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                className={`w-full h-12 pl-12 pr-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.phone ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                placeholder="Enter your 10-digit phone number"
              />
            </div>
            {errors.phone && <p className="text-destructive text-sm mt-1">{errors.phone}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="email"
                maxLength={255}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full h-12 pl-12 pr-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.email ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                placeholder="Enter your email (optional)"
              />
            </div>
            {errors.email && <p className="text-destructive text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Device */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Device Type
            </label>
            <select
              required
              value={formData.device}
              onChange={(e) => setFormData({ ...formData, device: e.target.value })}
              className={`w-full h-12 px-4 bg-secondary rounded-xl text-foreground focus:outline-none focus:ring-2 appearance-none cursor-pointer ${errors.device ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
            >
              <option value="">Select your device</option>
              {devices.map((device) => (
                <option key={device} value={device}>{device}</option>
              ))}
            </select>
            {errors.device && <p className="text-destructive text-sm mt-1">{errors.device}</p>}
          </div>

          {/* Issue */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Describe Issue
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-muted-foreground" />
              <textarea
                required
                maxLength={500}
                value={formData.issue}
                onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                className={`w-full min-h-[100px] pl-12 pr-4 py-3 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 resize-none ${errors.issue ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                placeholder="Describe the issue with your device (10-500 characters)"
              />
            </div>
            <div className="flex justify-between mt-1">
              {errors.issue && <p className="text-destructive text-sm">{errors.issue}</p>}
              <p className="text-muted-foreground text-xs ml-auto">{formData.issue.length}/500</p>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Preferred Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full h-12 pl-12 pr-4 bg-secondary rounded-xl text-foreground focus:outline-none focus:ring-2 ${errors.date ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                />
              </div>
              {errors.date && <p className="text-destructive text-sm mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Preferred Time
              </label>
              <select
                required
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className={`w-full h-12 px-4 bg-secondary rounded-xl text-foreground focus:outline-none focus:ring-2 appearance-none cursor-pointer ${errors.time ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
              >
                <option value="">Select time</option>
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              {errors.time && <p className="text-destructive text-sm mt-1">{errors.time}</p>}
            </div>
          </div>

          <Button type="submit" variant="apple-blue" className="w-full" size="lg">
            <Calendar className="w-5 h-5" />
            Confirm Booking
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
